import GrilleHeure from "./GrilleHeure";
import DayRdvs, { DayRdvsProps } from "./DayRdvs";
import { formaters } from "vincent-utils";
import { DateTime } from "luxon";
import _ from "underts";
import { Transforms } from "cadendar-shared";
import styles from "./AgendaColumn.module.css";
import classNames from "classnames";
import { useDroppable } from "@dnd-kit/core";

export type AgendaColumnProps<R extends { tag: string }> = {
  columnNumber: number;
  canManageStaffAbsences?: boolean;
  dayAbsenceSummaries?: Array<{
    absenceId: string;
    staffId: string;
    staffFullname: string;
    partOfDay: "full_day" | "morning" | "afternoon";
    leaveType: "paid" | "unpaid" | "special";
  }>;
  holidayName?: string | null;
  débutHeureAffichage: number;
  finHeureAffichage: number;
  duréeMiniRdv: number;
  now: Date;
  onDayHeaderClick?: (options: { event: React.MouseEvent<HTMLDivElement>; columnNumber: number }) => void;
} & Omit<DayRdvsProps<R>, "heightMultiplier"> & {
    onMouseEnter: (
      event: React.MouseEvent<HTMLDivElement>,
      rdvId: string | null,
      columnNumber?: number
    ) => void;
  };

const AgendaColumn = <R extends { tag: string }>(
  props: AgendaColumnProps<R>
) => {
  const { onCaseClick, onCloseCrossClick, onConfirmClick } = props;
  const { onMouseEnter, onMouseLeave } = props;
  const { rdvs } = props;
  const { holidayName } = props;
  const { columnNumber } = props;
  const { canManageStaffAbsences, dayAbsenceSummaries = [], onDayHeaderClick } = props;
  const { now } = props;
  const { débutHeureAffichage, finHeureAffichage, duréeMiniRdv } = props;
  const date = _.first(rdvs)?.date;
  const isToday =
    formaters.filterDateTimeForPureDate(now)!.getTime() === date?.getTime();
  const todayStr = date
    ? formaters.capitaliseEveryFirstLetter(
        DateTime.fromJSDate(date).toLocaleString({
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      )
    : "";
  const dayHeaderAnchorId = date
    ? `dayHeader-${DateTime.fromJSDate(date).toISODate()}`
    : `dayHeader-${columnNumber}`;
  const { setNodeRef } = useDroppable({ id: dayHeaderAnchorId });
  const heightMultiplier = Transforms.rdvs.getHeightMultiplier(duréeMiniRdv);
  const height = duréeMiniRdv * heightMultiplier;
  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    event.persist();
    onMouseEnter(event, null, columnNumber);
  };
  const handleMouseLeave = (event: React.MouseEvent) => {
    if (
      event.relatedTarget &&
      "id" in event.relatedTarget &&
      (event.relatedTarget as HTMLElement).id === "ColumnInfoPopup"
    ) {
      return;
    }
    onMouseLeave();
  };

  const heightMulClass =
    duréeMiniRdv < 15 ? styles.heightMulBig : styles.heightMultSmall;
  const hasAbsences = dayAbsenceSummaries.length > 0;
  const headerClassName = classNames({
    [styles.holidayHeader]: !!holidayName,
    [styles.headerClickable]: canManageStaffAbsences,
    [styles.headerWithAbsence]: hasAbsences,
  });
  const handleHeaderClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canManageStaffAbsences || !onDayHeaderClick) {
      return;
    }
    event.stopPropagation();
    onDayHeaderClick({ event, columnNumber });
  };
  const headerAnchorStyle = {
    anchorName: `--${dayHeaderAnchorId}`,
  };

  return (
    <div
      className={classNames(styles.agendacolumn, heightMulClass)}
      data-testid="AgendaColumn"
      id="AgendaColumnId"
    >
      {isToday ? (
        <div
          className={classNames([styles.today, "headerCol", headerClassName])}
          data-testid="todayCol"
          id={dayHeaderAnchorId}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleHeaderClick}
          ref={setNodeRef}
          //@ts-ignore anchorStyle pas encore dans le d.ts
          style={headerAnchorStyle}
        >
          <div>{todayStr}</div>
          {hasAbsences ? <div className={styles.absenceDot} /> : null}
        </div>
      ) : (
        <div
          className={classNames([styles.otherday, "headerCol", headerClassName])}
          data-testid="otherdayCol"
          id={dayHeaderAnchorId}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleHeaderClick}
          ref={setNodeRef}
          //@ts-ignore anchorStyle pas encore dans le d.ts
          style={headerAnchorStyle}
        >
          <div>{todayStr}</div>
          {hasAbsences ? <div className={styles.absenceDot} /> : null}
        </div>
      )}
      <div className={classNames([styles.spacer, "spacerCol"])}>
        <div className={styles.gridHourCol}>
          <GrilleHeure
            start={débutHeureAffichage}
            end={finHeureAffichage}
            step={duréeMiniRdv}
            height={height + "em"}
          />
        </div>
        <div className={classNames(styles.noPadding, styles.aptCol80)}>
          <DayRdvs
            rdvs={rdvs}
            onCaseClick={onCaseClick}
            onCloseCrossClick={onCloseCrossClick}
            onConfirmClick={onConfirmClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            now={now}
            heightMultiplier={heightMultiplier}
          />
        </div>
      </div>
    </div>
  );
};

export default AgendaColumn;
